/**
 * Shared trust check for analysis image URLs.
 *
 * Both the client (before calling /api/ai/analyze-grass) and the server
 * (analyze-grass + save-analysis routes) must agree that an `imageUrl` points
 * at our own Supabase public storage before it is:
 *   - forwarded to OpenAI as an `image_url` (OpenAI fetches it),
 *   - embedded in the prompt text, or
 *   - persisted into a public, shareable `analysis_history` row whose
 *     /result/[id] OpenGraph-image route fetches it SERVER-SIDE via next/og.
 *
 * The client used to be the only line of defense (an inline check in
 * useHomepageAnalysis), which let a crafted request POST an arbitrary URL —
 * a reachable server-side SSRF + stored-content-injection vector. This is the
 * single source of truth both sides import.
 */

const USER_UPLOADS_PATH = '/storage/v1/object/public/user-uploads/'

export interface TrustedImageUrlOptions {
  /**
   * Dev-only fallback hostname, applied ONLY when NEXT_PUBLIC_SUPABASE_URL is
   * unset. The client passes `window.location.hostname` so local dev (where
   * the storage origin can equal the app origin) still works. Never pass this
   * on the server in production.
   */
  devFallbackHostname?: string | null
}

export function isTrustedUserUploadsImageUrl(
  value: string | null | undefined,
  opts?: TrustedImageUrlOptions,
): boolean {
  if (!value || typeof value !== 'string') return false
  try {
    const parsed = new URL(value)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false

    // Must live under the public user-uploads storage path.
    if (!parsed.pathname.includes(USER_UPLOADS_PATH)) return false

    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
    const supabaseHost = supabaseUrl ? new URL(supabaseUrl).hostname : null
    if (supabaseHost && parsed.hostname === supabaseHost) {
      return true
    }

    // Development fallback when NEXT_PUBLIC_SUPABASE_URL is not configured.
    if (!supabaseHost && opts?.devFallbackHostname && parsed.hostname === opts.devFallbackHostname) {
      return true
    }

    return false
  } catch {
    return false
  }
}

/**
 * Backwards-compatible name for analysis callers. The trust boundary is the
 * shared public user-uploads bucket, not the analysis feature specifically.
 */
export const isTrustedAnalysisImageUrl = isTrustedUserUploadsImageUrl

/**
 * Community submissions must come from the upload route's dedicated prefix.
 * Keeping this narrower than the generic bucket check prevents callers from
 * publishing an existing scan, journal image, or another user's upload by URL.
 */
export function isTrustedCommunityImageUrl(
  value: string | null | undefined,
  kind: 'photo' | 'thumbnail',
): boolean {
  if (!value || typeof value !== 'string') return false

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  if (!supabaseUrl) return false

  try {
    const parsed = new URL(value)
    const trustedOrigin = new URL(supabaseUrl).origin

    // `image_url` has a database uniqueness contract, so accept one canonical
    // representation only. Otherwise query strings, scheme changes, encoded
    // path aliases, or credentials could point at the same object while
    // bypassing the raw-text unique index.
    if (parsed.protocol !== 'https:' || parsed.origin !== trustedOrigin) return false
    if (parsed.username || parsed.password || parsed.search || parsed.hash) return false
    if (value !== parsed.toString()) return false

    const prefix = `${USER_UPLOADS_PATH}lawn-photos/`
    if (!parsed.pathname.startsWith(prefix)) return false

    const path = parsed.pathname.slice(USER_UPLOADS_PATH.length)
    const safeFileName = '[a-zA-Z0-9][a-zA-Z0-9._-]*'
    const expectedPath = kind === 'thumbnail'
      ? new RegExp(`^lawn-photos/thumbnails/${safeFileName}$`)
      : new RegExp(`^lawn-photos/${safeFileName}$`)

    return expectedPath.test(path)
  } catch {
    return false
  }
}

/**
 * Extract the storage object path (everything after the public `user-uploads/`
 * segment) from a trusted analysis image URL, e.g.
 *   https://<proj>.supabase.co/storage/v1/object/public/user-uploads/grass-analysis/x.jpg
 *   -> "grass-analysis/x.jpg"
 *
 * Returns null if the URL doesn't contain the expected public-uploads segment.
 * Used to download the object directly via the Supabase Storage SDK instead of
 * re-fetching the public URL over HTTP. Pairs with isTrustedAnalysisImageUrl().
 */
export function extractUserUploadsStoragePath(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null
  const marker = USER_UPLOADS_PATH // '/storage/v1/object/public/user-uploads/'
  const idx = value.indexOf(marker)
  if (idx === -1) return null
  const rest = value.slice(idx + marker.length)
  // Strip any query string / hash, then decode (storage paths are URL-encoded).
  const pathOnly = rest.split(/[?#]/)[0]
  if (!pathOnly) return null
  try {
    return decodeURIComponent(pathOnly)
  } catch {
    return pathOnly
  }
}

/** Bucket that backs the public user-uploads storage path. */
export const USER_UPLOADS_BUCKET = 'user-uploads'
